import React, { useCallback, useEffect, useMemo, useState } from "react";
import { apiRequest } from "../../helpers/api";
import { AccessPanelSkeleton } from "../../components/skeleton/PageSkeletons";
import { showError, showSuccess } from "../../components/ToastNotifications";
import { useAccess } from "../../components/AccessContext";
import "./AccessControl.css";

const SUPER_ADMIN = "super";
const groupIcons = { admin: "ti-shield-lock", user: "ti-building-store" };
const moduleIcons = {
  "admin.dashboard": "ti-layout-dashboard",
  "admin.users": "ti-users",
  "admin.businesses": "ti-building-store",
  "admin.support": "ti-lifebuoy",
  "admin.subscriptions": "ti-ticket",
  "admin.integrations": "ti-plug-connected",
  "admin.ai_providers": "ti-sparkles",
  "admin.access_control": "ti-shield-lock",
  "admin.payment_gateways": "ti-credit-card",
  "user.dashboard": "ti-layout-dashboard",
  "user.posts": "ti-notes",
  "user.create_content": "ti-wand",
  "user.content_calendar": "ti-calendar-event",
  "user.media_library": "ti-photo",
  "user.social_accounts": "ti-brand-instagram",
  "user.messages": "ti-message-circle",
  "user.leads": "ti-user-star",
  "user.analytics": "ti-chart-bar",
  "user.business_profile": "ti-id-badge-2",
  "user.settings": "ti-settings",
  "user.subscriptions": "ti-rocket",
  "user.billing": "ti-receipt"
};

const formatDate = (value) => value ? new Date(value).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—";
const formatDateTime = (value) => value ? new Date(value).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "";
const plural = (count, word) => `${count} ${word}${count === 1 ? "" : "s"}`;

// Builds dependency helpers from the server catalog: every action implies its module's "view",
// plus any permissions the catalog says it requires.
const buildPermissionModel = (catalog) => {
  const modules = (catalog?.groups || []).flatMap((group) => group.modules.map((module) => ({ ...module, group: group.id })));
  const actionByKey = new Map(modules.flatMap((module) => module.actions.map((action) => [action.key, { ...action, module }])));
  const directRequirements = (key) => {
    const entry = actionByKey.get(key);
    if (!entry) return [];
    const viewKey = `${entry.module.key}.view`;
    return [...(entry.id !== "view" ? [viewKey] : []), ...entry.requires];
  };
  const withRequirements = (keys) => {
    const result = new Set();
    const pending = [...keys];
    while (pending.length) {
      const key = pending.pop();
      if (result.has(key) || !actionByKey.has(key)) continue;
      result.add(key);
      pending.push(...directRequirements(key));
    }
    return result;
  };
  const add = (permissions, keys) => [...withRequirements([...permissions, ...keys])];
  // Removing a permission also removes everything that depends on it.
  const remove = (permissions, keys) => {
    const removed = new Set(keys);
    return permissions.filter((permission) => !removed.has(permission) && ![...withRequirements([permission])].some((key) => removed.has(key)));
  };
  return { modules, actionByKey, add, remove };
};

const summarize = (permissions, model) => {
  const byModule = new Map();
  for (const permission of permissions) {
    const entry = model.actionByKey.get(permission);
    if (!entry) continue;
    const list = byModule.get(entry.module.key) || { module: entry.module, actions: [] };
    list.actions.push(entry.name);
    byModule.set(entry.module.key, list);
  }
  return [...byModule.values()];
};

const describeActivity = (item) => {
  const changes = item.changes || {};
  switch (item.action) {
    case "ADMIN_ROLE_CREATED": return <>created the <strong>{changes.name}</strong> role</>;
    case "ADMIN_ROLE_UPDATED": {
      const parts = [];
      if (changes.name) parts.push(`renamed it from "${changes.name.from}" to "${changes.name.to}"`);
      if (changes.permissions) parts.push(`${plural(changes.permissions.added.length, "permission")} added, ${changes.permissions.removed.length} removed`);
      if (changes.description) parts.push("updated the description");
      return <>updated a role{parts.length ? ` — ${parts.join("; ")}` : ""}</>;
    }
    case "ADMIN_ROLE_DELETED": return <>deleted the <strong>{changes.name}</strong> role</>;
    case "ADMIN_MEMBER_INVITED": return <>invited <strong>{item.target}</strong> as {changes.role}</>;
    case "ADMIN_MEMBER_INVITE_RESENT": return <>resent the invitation to <strong>{item.target}</strong></>;
    case "ADMIN_MEMBER_UPDATED": {
      const parts = [];
      if (changes.role) parts.push(`role changed from ${changes.role.from} to ${changes.role.to}`);
      if (changes.accountStatus) parts.push(changes.accountStatus.to === "suspended" ? "suspended access" : "restored access");
      return <>updated <strong>{item.target}</strong>{parts.length ? ` — ${parts.join("; ")}` : ""}</>;
    }
    case "CUSTOMER_ROLE_UPDATED": return <>changed the workspace role of customer <strong>{item.target}</strong> from {changes.role?.from} to {changes.role?.to}</>;
    default: return <>made an access change</>;
  }
};

const AccessControl = () => {
  const { access, can } = useAccess();
  const [tab, setTab] = useState("members");
  const [catalog, setCatalog] = useState(null);
  const [roles, setRoles] = useState([]);
  const [members, setMembers] = useState([]);
  const [activity, setActivity] = useState([]);
  const [superAdminCount, setSuperAdminCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [roleEditor, setRoleEditor] = useState(null);
  const [memberEditor, setMemberEditor] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [saving, setSaving] = useState(false);
  const model = useMemo(() => buildPermissionModel(catalog), [catalog]);
  const isSuperAdmin = Boolean(access?.isSuperAdmin);
  const canCreate = can("admin.access_control.create");
  const canEditRoles = can("admin.access_control.edit");
  const canDeleteRoles = can("admin.access_control.delete");
  const canChangeStatus = can("admin.access_control.change_status");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [roleResult, memberResult, activityResult] = await Promise.all([
        apiRequest("/api/admin/access-control/roles"),
        apiRequest("/api/admin/access-control/members"),
        apiRequest("/api/admin/access-control/activity")
      ]);
      setCatalog(roleResult.catalog);
      setRoles(roleResult.roles || []);
      setSuperAdminCount(roleResult.superAdminCount || 0);
      setMembers(memberResult.members || []);
      setActivity(activityResult.activity || []);
    } catch (requestError) {
      setError(requestError.message || "Unable to load roles and team members.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!roleEditor && !memberEditor && !confirmDelete) return undefined;
    const closeOnEscape = (event) => {
      if (event.key !== "Escape" || saving) return;
      setRoleEditor(null);
      setMemberEditor(null);
      setConfirmDelete(null);
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [roleEditor, memberEditor, confirmDelete, saving]);

  const filteredMembers = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return members;
    return members.filter((member) => [member.name, member.email, member.role?.name, member.isSuperAdmin ? "super admin" : ""]
      .some((value) => String(value || "").toLowerCase().includes(query)));
  }, [members, search]);

  const staffRoles = roles.filter((role) => role.appliesTo.staff && role.canManage);
  const stats = {
    members: members.length,
    superAdmins: superAdminCount,
    roles: roles.length,
    customers: roles.reduce((total, role) => total + role.customerCount, 0)
  };

  // Admin permissions the signed-in admin may hand out; user-module permissions are always grantable.
  const canGrant = (key) => isSuperAdmin || key.startsWith("user.") || can(key);

  const openRoleEditor = (role, duplicate = false) => setRoleEditor({
    id: duplicate ? null : role?.id || null,
    name: role ? duplicate ? `${role.name} copy`.slice(0, 60) : role.name : "",
    description: role?.description || "",
    permissions: role?.permissions || []
  });

  const saveRole = async (event) => {
    event.preventDefault();
    if (!roleEditor.permissions.length) return showError("Grant at least one permission to this role.");
    setSaving(true);
    try {
      const body = JSON.stringify({ name: roleEditor.name, description: roleEditor.description, permissions: roleEditor.permissions });
      const result = roleEditor.id
        ? await apiRequest(`/api/admin/access-control/roles/${roleEditor.id}`, { method: "PUT", body })
        : await apiRequest("/api/admin/access-control/roles", { method: "POST", body });
      showSuccess(result.message);
      setRoleEditor(null);
      await load();
    } catch (requestError) {
      showError(requestError.message || "Unable to save this role.");
    } finally {
      setSaving(false);
    }
  };

  const deleteRole = async () => {
    setSaving(true);
    try {
      const result = await apiRequest(`/api/admin/access-control/roles/${confirmDelete.id}`, { method: "DELETE" });
      showSuccess(result.message);
      setConfirmDelete(null);
      await load();
    } catch (requestError) {
      showError(requestError.message || "Unable to delete this role.");
    } finally {
      setSaving(false);
    }
  };

  const openInvite = () => setMemberEditor({ mode: "invite", firstName: "", lastName: "", email: "", roleId: staffRoles[0] ? String(staffRoles[0].id) : isSuperAdmin ? SUPER_ADMIN : "" });
  const openMember = (member) => setMemberEditor({
    mode: "edit",
    member,
    roleId: member.isSuperAdmin ? SUPER_ADMIN : String(member.role?.id || ""),
    accountStatus: member.accountStatus === "suspended" ? "suspended" : "active"
  });

  const saveMember = async (event) => {
    event.preventDefault();
    if (!memberEditor.roleId) return showError("Choose a role for this team member.");
    setSaving(true);
    try {
      const roleId = memberEditor.roleId === SUPER_ADMIN ? null : Number(memberEditor.roleId);
      if (memberEditor.mode === "invite") {
        const result = await apiRequest("/api/admin/access-control/members", {
          method: "POST",
          body: JSON.stringify({ firstName: memberEditor.firstName, lastName: memberEditor.lastName, email: memberEditor.email, roleId })
        });
        if (result.inviteDelivered) {
          showSuccess(result.message);
          setMemberEditor(null);
        } else {
          setMemberEditor({ mode: "link", email: result.member.email, setupUrl: result.setupUrl });
        }
      } else {
        const body = {};
        const currentRoleId = memberEditor.member.isSuperAdmin ? SUPER_ADMIN : String(memberEditor.member.role?.id || "");
        if (canEditRoles && memberEditor.roleId !== currentRoleId) body.roleId = roleId;
        if (canChangeStatus && memberEditor.accountStatus !== memberEditor.member.accountStatus) body.accountStatus = memberEditor.accountStatus;
        if (!Object.keys(body).length) {
          setMemberEditor(null);
          return;
        }
        const result = await apiRequest(`/api/admin/access-control/members/${memberEditor.member.id}`, { method: "PATCH", body: JSON.stringify(body) });
        showSuccess(result.message);
        setMemberEditor(null);
      }
      await load();
    } catch (requestError) {
      showError(requestError.message || "Unable to save this team member.");
    } finally {
      setSaving(false);
    }
  };

  const resendInvite = async (member) => {
    setSaving(true);
    try {
      const result = await apiRequest(`/api/admin/access-control/members/${member.id}/resend-invite`, { method: "POST" });
      if (result.inviteDelivered) showSuccess(result.message);
      else setMemberEditor({ mode: "link", email: member.email, setupUrl: result.setupUrl });
      await load();
    } catch (requestError) {
      showError(requestError.message || "Unable to resend the invitation.");
    } finally {
      setSaving(false);
    }
  };

  const closeModals = () => {
    if (saving) return;
    setRoleEditor(null);
    setMemberEditor(null);
    setConfirmDelete(null);
  };

  const memberRoleOptions = (member) => {
    const options = staffRoles.map((role) => ({ value: String(role.id), label: role.name }));
    if (member && !member.isSuperAdmin && member.role && !options.some((option) => option.value === String(member.role.id))) {
      options.unshift({ value: String(member.role.id), label: member.role.name });
    }
    if (isSuperAdmin) options.push({ value: SUPER_ADMIN, label: "Super admin — full access" });
    return options;
  };

  return (
    <section className="workspace-dashboard admin-rbac">
      <header className="workspace-dashboard-header">
        <div>
          <p className="workspace-eyebrow">ACCESS CONTROL</p>
          <h1>Roles &amp; permissions</h1>
          <p className="workspace-intro">Create roles that decide what your team can do in the admin portal and what customers can do in their workspace.</p>
        </div>
        {canCreate && <div className="admin-rbac-header-actions">
          <button type="button" className="admin-rbac-secondary" onClick={() => { setTab("roles"); openRoleEditor(null); }} disabled={loading}><i className="ti ti-shield-plus" aria-hidden="true" /> Create role</button>
          <button type="button" className="admin-rbac-primary" onClick={openInvite} disabled={loading || (!staffRoles.length && !isSuperAdmin)}><i className="ti ti-user-plus" aria-hidden="true" /> Invite member</button>
        </div>}
      </header>

      {error && <div className="admin-rbac-error" role="alert"><i className="ti ti-alert-circle" aria-hidden="true" /><span>{error}</span><button type="button" onClick={load}>Retry</button></div>}

      <div className="admin-rbac-stats">
        {[
          ["Team members", stats.members, "ti-users-group"],
          ["Super admins", stats.superAdmins, "ti-crown"],
          ["Roles", stats.roles, "ti-shield-check"],
          ["Customers with a role", stats.customers, "ti-building-store"]
        ].map(([label, value, icon]) => <article key={label}><span><i className={`ti ${icon}`} aria-hidden="true" /></span><div><strong>{loading ? "—" : value}</strong><small>{label}</small></div></article>)}
      </div>

      <div className="admin-rbac-tabs" role="tablist" aria-label="Access control sections">
        {[["members", "Team members", stats.members], ["roles", "Roles", stats.roles + 1], ["activity", "Activity", null]].map(([id, label, count]) => (
          <button key={id} type="button" role="tab" aria-selected={tab === id} className={tab === id ? "is-active" : ""} onClick={() => setTab(id)}>
            {label}{count !== null && !loading && <span>{count}</span>}
          </button>
        ))}
      </div>

      {loading ? <AccessPanelSkeleton /> : <>
        {tab === "members" && <section className="admin-rbac-panel" aria-label="Team members">
          <div className="admin-rbac-panel-head">
            <div><h2>Team members</h2><p>Everyone who can sign in to the admin portal. Changes apply on their next action — no sign-out needed. Customer roles are assigned from each customer's page under Users.</p></div>
            <label className="admin-rbac-search"><i className="ti ti-search" aria-hidden="true" /><input aria-label="Search team members" placeholder="Search name, email or role" value={search} onChange={(event) => setSearch(event.target.value)} /></label>
          </div>
          {filteredMembers.length ? <div className="admin-rbac-table-wrap"><table className="admin-rbac-table">
            <thead><tr><th>Member</th><th>Role</th><th>Status</th><th>Added</th><th aria-label="Actions" /></tr></thead>
            <tbody>{filteredMembers.map((member) => <tr key={member.id}>
              <td><div className="admin-rbac-member"><span aria-hidden="true">{(member.name || member.email).slice(0, 1).toUpperCase()}</span><div><strong>{member.name}{member.isCurrentUser && <em>You</em>}</strong><small>{member.email}</small></div></div></td>
              <td>{member.isSuperAdmin
                ? <span className="admin-rbac-role-badge is-super"><i className="ti ti-crown" aria-hidden="true" /> Super admin</span>
                : <span className="admin-rbac-role-badge">{member.role?.name || "No role"}</span>}</td>
              <td>{member.accountStatus === "suspended"
                ? <span className="admin-rbac-status is-suspended">Suspended</span>
                : member.invitePending ? <span className="admin-rbac-status is-pending">Invite pending</span> : <span className="admin-rbac-status is-active">Active</span>}</td>
              <td>{formatDate(member.createdAt)}</td>
              <td><div className="admin-rbac-row-actions">
                {member.canManage && canCreate && member.invitePending && member.accountStatus === "active" && <button type="button" onClick={() => resendInvite(member)} disabled={saving}>Resend invite</button>}
                {member.isCurrentUser
                  ? <span className="admin-rbac-muted" title="Ask another administrator to change your access">Your account</span>
                  : member.canManage && (canEditRoles || canChangeStatus)
                    ? <button type="button" onClick={() => openMember(member)} aria-label={`Edit access for ${member.name}`}>Edit access</button>
                    : <span className="admin-rbac-muted" title="This member has access your role can't change">View only</span>}
              </div></td>
            </tr>)}</tbody>
          </table></div> : <div className="admin-rbac-empty"><i className="ti ti-users" aria-hidden="true" /><h3>{search ? "No matching team members" : "No team members yet"}</h3><p>{search ? "Try a different name, email or role." : "Invite a teammate to share the admin workload."}</p></div>}
        </section>}

        {tab === "roles" && <section className="admin-rbac-panel" aria-label="Roles">
          <div className="admin-rbac-panel-head">
            <div><h2>Roles</h2><p>A role is a reusable set of permissions. Admin-module permissions apply to team members; user-module permissions apply to customer accounts assigned the role.</p></div>
            {canCreate && <button type="button" className="admin-rbac-primary" onClick={() => openRoleEditor(null)}><i className="ti ti-plus" aria-hidden="true" /> New role</button>}
          </div>
          <div className="admin-rbac-role-grid">
            <article className="admin-rbac-role-card is-system">
              <header><span className="admin-rbac-role-icon"><i className="ti ti-crown" aria-hidden="true" /></span><div><h3>Super admin</h3><small>System role · {plural(stats.superAdmins, "member")}</small></div></header>
              <p>Full access to every admin and user module, including super-admin-only areas such as payment gateways. Can't be edited or deleted.</p>
              <div className="admin-rbac-chips"><span className="admin-rbac-chip is-manage">All admin modules</span><span className="admin-rbac-chip is-manage">All user modules</span></div>
            </article>
            {roles.map((role) => {
              const summary = summarize(role.permissions, model);
              const shown = summary.slice(0, 6);
              return <article className="admin-rbac-role-card" key={role.id}>
                <header><span className="admin-rbac-role-icon"><i className="ti ti-shield-check" aria-hidden="true" /></span><div><h3>{role.name}</h3><small>{plural(role.staffCount, "team member")} · {plural(role.customerCount, "customer")}</small></div></header>
                <p>{role.description || "No description"}</p>
                <div className="admin-rbac-applies">
                  {role.appliesTo.staff && <span><i className="ti ti-shield-lock" aria-hidden="true" /> Team members</span>}
                  {role.appliesTo.customers && <span><i className="ti ti-building-store" aria-hidden="true" /> Customers</span>}
                </div>
                <div className="admin-rbac-chips">
                  {shown.map(({ module, actions }) => <span key={module.key} className={`admin-rbac-chip is-${module.group}`} title={actions.join(", ")}>{module.name} · {actions.length === module.actions.length ? "All" : actions.length}</span>)}
                  {summary.length > shown.length && <span className="admin-rbac-chip">+{summary.length - shown.length} more</span>}
                </div>
                <footer>
                  {role.canManage && canEditRoles && <button type="button" onClick={() => openRoleEditor(role)} aria-label={`Edit ${role.name}`}><i className="ti ti-pencil" aria-hidden="true" /> Edit</button>}
                  {canCreate && <button type="button" onClick={() => openRoleEditor(role, true)} aria-label={`Duplicate ${role.name}`}><i className="ti ti-copy" aria-hidden="true" /> Duplicate</button>}
                  {role.canManage && canDeleteRoles && <button type="button" className="is-danger" onClick={() => setConfirmDelete(role)} aria-label={`Delete ${role.name}`}><i className="ti ti-trash" aria-hidden="true" /> Delete</button>}
                  {!role.canManage && <span className="admin-rbac-muted"><i className="ti ti-lock" aria-hidden="true" /> Includes access beyond your role</span>}
                </footer>
              </article>;
            })}
          </div>
          {!roles.length && <div className="admin-rbac-note"><i className="ti ti-bulb" aria-hidden="true" />Create a role such as "Support agent" for your team, or "Content creator" to limit what a customer account can do.</div>}
        </section>}

        {tab === "activity" && <section className="admin-rbac-panel" aria-label="Access activity">
          <div className="admin-rbac-panel-head"><div><h2>Recent access changes</h2><p>Every role, team-member and customer-role change is recorded with who made it and when.</p></div></div>
          {activity.length ? <ol className="admin-rbac-activity">{activity.map((item) => <li key={item.id}>
            <span className="admin-rbac-activity-icon" aria-hidden="true"><i className={`ti ${item.entityType === "ADMIN_ROLE" ? "ti-shield" : item.entityType === "USER" ? "ti-building-store" : "ti-user"}`} /></span>
            <div><p><strong>{item.actor}</strong> {describeActivity(item)}</p><small>{formatDateTime(item.createdAt)}</small></div>
          </li>)}</ol> : <div className="admin-rbac-empty"><i className="ti ti-history" aria-hidden="true" /><h3>No access changes yet</h3><p>Role and team updates will appear here.</p></div>}
        </section>}
      </>}

      {roleEditor && <Modal title={roleEditor.id ? "Edit role" : "Create role"} eyebrow="Role permissions" onClose={closeModals} saving={saving} wide>
        <form onSubmit={saveRole}>
          <div className="admin-rbac-form-grid">
            <label className="admin-rbac-field"><span>Role name</span><input autoFocus required minLength={2} maxLength={60} value={roleEditor.name} onChange={(event) => setRoleEditor({ ...roleEditor, name: event.target.value })} placeholder="e.g. Support agent" /></label>
            <label className="admin-rbac-field"><span>Description <small>Optional</small></span><input maxLength={255} value={roleEditor.description} onChange={(event) => setRoleEditor({ ...roleEditor, description: event.target.value })} placeholder="What this role is responsible for" /></label>
          </div>
          <PermissionMatrix catalog={catalog} model={model} permissions={roleEditor.permissions} canGrant={canGrant} onChange={(permissions) => setRoleEditor((current) => ({ ...current, permissions }))} />
          <div className="admin-rbac-modal-actions">
            <button type="button" className="admin-rbac-secondary" onClick={closeModals} disabled={saving}>Cancel</button>
            <button type="submit" className="admin-rbac-primary" disabled={saving}>{saving ? "Saving…" : roleEditor.id ? "Save changes" : "Create role"}</button>
          </div>
        </form>
      </Modal>}

      {memberEditor && memberEditor.mode !== "link" && <Modal title={memberEditor.mode === "invite" ? "Invite team member" : `Edit access · ${memberEditor.member.name}`} eyebrow={memberEditor.mode === "invite" ? "New admin account" : memberEditor.member.email} onClose={closeModals} saving={saving}>
        <form onSubmit={saveMember}>
          {memberEditor.mode === "invite" && <>
            <div className="admin-rbac-form-grid">
              <label className="admin-rbac-field"><span>First name</span><input autoFocus required maxLength={80} value={memberEditor.firstName} onChange={(event) => setMemberEditor({ ...memberEditor, firstName: event.target.value })} /></label>
              <label className="admin-rbac-field"><span>Last name <small>Optional</small></span><input maxLength={80} value={memberEditor.lastName} onChange={(event) => setMemberEditor({ ...memberEditor, lastName: event.target.value })} /></label>
            </div>
            <label className="admin-rbac-field"><span>Work email</span><input required type="email" maxLength={254} value={memberEditor.email} onChange={(event) => setMemberEditor({ ...memberEditor, email: event.target.value })} placeholder="name@company.com" /></label>
          </>}
          {(memberEditor.mode === "invite" || canEditRoles) && <label className="admin-rbac-field"><span>Role</span>
            <select value={memberEditor.roleId} onChange={(event) => setMemberEditor({ ...memberEditor, roleId: event.target.value })} required>
              {!memberEditor.roleId && <option value="">Choose a role</option>}
              {memberRoleOptions(memberEditor.member).map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
            </select>
          </label>}
          <RolePreview roleId={memberEditor.roleId} roles={roles} model={model} />
          {memberEditor.mode === "edit" && canChangeStatus && <fieldset className="admin-rbac-status-choice">
            <legend>Account access</legend>
            {[["active", "Active", "Can sign in and use the sections their role allows."], ["suspended", "Suspended", "Signed out immediately and can't sign in until restored."]].map(([value, label, hint]) => (
              <label key={value} className={memberEditor.accountStatus === value ? "is-selected" : ""}>
                <input type="radio" name="accountStatus" value={value} checked={memberEditor.accountStatus === value} onChange={() => setMemberEditor({ ...memberEditor, accountStatus: value })} />
                <span><strong>{label}</strong><small>{hint}</small></span>
              </label>
            ))}
          </fieldset>}
          {memberEditor.mode === "invite" && <div className="admin-rbac-note"><i className="ti ti-mail" aria-hidden="true" />We'll email a secure link so they can set their own password. The link expires in 72 hours.</div>}
          <div className="admin-rbac-modal-actions">
            <button type="button" className="admin-rbac-secondary" onClick={closeModals} disabled={saving}>Cancel</button>
            <button type="submit" className="admin-rbac-primary" disabled={saving}>{saving ? "Saving…" : memberEditor.mode === "invite" ? "Send invitation" : "Save changes"}</button>
          </div>
        </form>
      </Modal>}

      {memberEditor?.mode === "link" && <Modal title="Share the setup link" eyebrow="Email not delivered" onClose={() => setMemberEditor(null)}>
        <div className="admin-rbac-warning"><i className="ti ti-alert-triangle" aria-hidden="true" />The invitation email to <strong>{memberEditor.email}</strong> couldn't be sent, so check your email (SMTP) settings. Share this one-time link privately; it expires in 72 hours.</div>
        <div className="admin-rbac-link-box">
          <input readOnly aria-label="Account setup link" value={memberEditor.setupUrl} onFocus={(event) => event.target.select()} />
          <button type="button" className="admin-rbac-primary" onClick={() => navigator.clipboard?.writeText(memberEditor.setupUrl).then(() => showSuccess("Setup link copied."), () => showError("Copy the link manually."))}><i className="ti ti-copy" aria-hidden="true" /> Copy</button>
        </div>
        <div className="admin-rbac-modal-actions"><button type="button" className="admin-rbac-secondary" onClick={() => setMemberEditor(null)}>Done</button></div>
      </Modal>}

      {confirmDelete && <Modal title={`Delete ${confirmDelete.name}?`} eyebrow="Delete role" onClose={closeModals} saving={saving}>
        {confirmDelete.staffCount + confirmDelete.customerCount > 0
          ? <div className="admin-rbac-warning"><i className="ti ti-alert-triangle" aria-hidden="true" />{plural(confirmDelete.staffCount, "team member")} and {plural(confirmDelete.customerCount, "customer")} use this role. Move them to another role first.</div>
          : <p className="admin-rbac-confirm-copy">This permanently removes the role. Nobody is assigned to it, so no one's access changes.</p>}
        <div className="admin-rbac-modal-actions">
          <button type="button" className="admin-rbac-secondary" onClick={closeModals} disabled={saving}>Cancel</button>
          <button type="button" className="admin-rbac-danger" onClick={deleteRole} disabled={saving || confirmDelete.staffCount + confirmDelete.customerCount > 0}>{saving ? "Deleting…" : "Delete role"}</button>
        </div>
      </Modal>}
    </section>
  );
};

const Modal = ({ title, eyebrow, onClose, saving, wide, children }) => (
  <div className="admin-rbac-modal-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget && !saving) onClose(); }}>
    <section className={`admin-rbac-modal${wide ? " is-wide" : ""}`} role="dialog" aria-modal="true" aria-labelledby="admin-rbac-modal-title">
      <div className="admin-rbac-modal-heading">
        <div><p className="workspace-eyebrow">{eyebrow}</p><h2 id="admin-rbac-modal-title">{title}</h2></div>
        <button type="button" aria-label="Close" onClick={onClose} disabled={saving}><i className="ti ti-x" aria-hidden="true" /></button>
      </div>
      {children}
    </section>
  </div>
);

const PermissionMatrix = ({ catalog, model, permissions, canGrant, onChange }) => {
  const granted = new Set(permissions);
  const grantableKeys = (modules) => modules.filter((module) => !module.superAdminOnly).flatMap((module) => module.actions.map((action) => action.key)).filter(canGrant);
  const toggle = (key, checked) => onChange(checked ? model.add(permissions, [key]) : model.remove(permissions, [key]));
  const setMany = (keys, checked) => onChange(checked ? model.add(permissions, keys) : model.remove(permissions, keys));

  return <div className="admin-rbac-matrix-groups">
    {catalog.groups.map((group) => {
      const keys = grantableKeys(group.modules);
      const groupGranted = group.modules.flatMap((module) => module.actions).filter((action) => granted.has(action.key)).length;
      const groupTotal = group.modules.filter((module) => !module.superAdminOnly).flatMap((module) => module.actions).length;
      return <section className={`admin-rbac-matrix is-${group.id}`} key={group.id} aria-labelledby={`admin-rbac-group-${group.id}`}>
        <div className="admin-rbac-matrix-head">
          <span className="admin-rbac-group-icon" aria-hidden="true"><i className={`ti ${groupIcons[group.id] || "ti-box"}`} /></span>
          <div><h3 id={`admin-rbac-group-${group.id}`}>{group.name}</h3><p>{group.description} · {groupGranted} of {groupTotal} granted</p></div>
          <div className="admin-rbac-quick">
            <button type="button" onClick={() => setMany(keys, true)} disabled={!keys.length} aria-label={`Select all ${group.name}`}>Select all</button>
            <button type="button" onClick={() => setMany(keys, false)} disabled={!keys.length} aria-label={`Clear all ${group.name}`}>Clear all</button>
          </div>
        </div>
        <div className="admin-rbac-matrix-rows">{group.modules.map((module) => {
          const moduleKeys = module.superAdminOnly ? [] : module.actions.map((action) => action.key).filter(canGrant);
          const moduleGranted = module.actions.filter((action) => granted.has(action.key)).length;
          return <div className={`admin-rbac-matrix-row${moduleGranted ? " is-granted" : ""}${module.superAdminOnly ? " is-locked" : ""}`} key={module.key} role="group" aria-label={`${module.name} permissions`}>
            <span className="admin-rbac-module-icon" aria-hidden="true"><i className={`ti ${moduleIcons[module.key] || "ti-box"}`} /></span>
            <div className="admin-rbac-module-copy"><strong>{module.name}</strong><small>{module.description}</small></div>
            {module.superAdminOnly
              ? <span className="admin-rbac-locked"><i className="ti ti-lock" aria-hidden="true" /> Super admin only</span>
              : <>
                <div className="admin-rbac-actions">{module.actions.map((action) => {
                  const allowed = canGrant(action.key);
                  return <label key={action.key} className={`admin-rbac-action${granted.has(action.key) ? " is-checked" : ""}${allowed ? "" : " is-disabled"}`} title={allowed ? action.help || undefined : "Your role doesn't include this permission, so you can't grant it."}>
                    <input type="checkbox" checked={granted.has(action.key)} disabled={!allowed} onChange={(event) => toggle(action.key, event.target.checked)} aria-label={`${module.name}: ${action.name}`} />
                    <span>{action.name}</span>
                  </label>;
                })}</div>
                <div className="admin-rbac-row-quick">
                  <button type="button" onClick={() => setMany(moduleKeys, true)} disabled={!moduleKeys.length} aria-label={`Select all ${module.name}`}>All</button>
                  <button type="button" onClick={() => setMany(moduleKeys, false)} disabled={!moduleKeys.length || !moduleGranted} aria-label={`Clear ${module.name}`}>None</button>
                </div>
              </>}
          </div>;
        })}</div>
      </section>;
    })}
    {catalog.alwaysAvailable?.length > 0 && <div className="admin-rbac-note"><i className="ti ti-info-circle" aria-hidden="true" />Always available to every customer, whatever their role: {catalog.alwaysAvailable.join(", ")}. Customer roles also never unlock features their subscription plan doesn't include.</div>}
  </div>;
};

const RolePreview = ({ roleId, roles, model }) => {
  if (roleId === SUPER_ADMIN) return <div className="admin-rbac-preview is-super"><i className="ti ti-crown" aria-hidden="true" /><span>Full access to every module, including roles and super-admin-only areas. Grant this only to people you fully trust.</span></div>;
  const role = roles.find((item) => String(item.id) === String(roleId));
  if (!role) return null;
  const adminModules = summarize(role.permissions.filter((permission) => permission.startsWith("admin.")), model);
  return <div className="admin-rbac-preview"><span>In the admin portal, this role can use:</span><div className="admin-rbac-chips">{adminModules.map(({ module, actions }) => <span key={module.key} className="admin-rbac-chip is-admin" title={actions.join(", ")}>{module.name} · {actions.join(", ")}</span>)}</div></div>;
};

export default AccessControl;
